import React, { Suspense, useRef, useCallback, useLayoutEffect, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { pageSlideVariants } from "@/shared/motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useUrlParams } from "@/hooks/useUrlParams";
import { appendPersistedQueryParams } from "@/shared/utils/campaignParams";
import { applyCanonicalBookingUrl, ROUTE_SLUGS, SCREEN_SLUGS } from "@/shared/utils/bookingUrl";
import { useTrackContext } from "@/state/TrackContext";
import { BookingAnalytics } from "@/shared/utils/bookingAnalytics";
import TrackSelection from "@/components/TrackSelection";
import RacingTrackSelection from "@/components/RacingTrackSelection";
import { BATTLEKART_ENABLED, BATTLEKART_CLOSED_URL, shouldSkipExperienceSelector } from "@/consts/featureFlags";
import ProgressBar from "@/components/ProgressiveBar";
import { GlobalBottomSummary } from "@/components/BottomSummary/BottomSummary";
import { usePathwayStyling } from "@/hooks/usePathwayStyling";
import { getVisibilityConfig } from "@/shared/utils/stylingConfigOperations";
import {
  AddAnotherAttraction,
  ArcadeCard,
  ArcadeCart,
  ArcadeExperience,
  BattleKart,
  BattleKartPlayersAndSessions,
  BattleKartActiveKids,
  BattleKartPostCode,
  IntermediateTrackActiveKids,
  IntermediateTrackPostCode,
  IntermediateTrackReviewExperience,
  IntermediateTrackSelectDateTime,
  IntermediateTrackSelectSeatsRaces,
  JuniorTrack,
  JuniorTrackActiveKids,
  JuniorTrackPostCode,
  JuniorTrackReviewExperience,
  JuniorTrackSelectSeatsRaces,
  MainActiveKids,
  MainPostCode,
  MiniActiveKids,
  MiniPostCode,
  MiniReviewExperience,
  MiniSelectDateTime,
  MiniSelectSeatsRaces,
  ReviewExperience,
  ScreenFallback,
  SelectDateTime,
  SelectSeatsRaces,
} from "@/app/lazyScreens";

/**
 * Step-index map for determining navigation direction (forward vs backward).
 *
 * Each screen in the booking flow maps to a numeric step. When the user navigates,
 * we compare the new step index with the previous one:
 *   - Higher index → forward (direction = 1)  → content slides UP (enters from below)
 *   - Lower index  → backward (direction = -1) → content slides DOWN (enters from above)
 *
 * IMPORTANT: indices must reflect the actual on-screen flow order so transitions
 * always swipe up when the user advances. Screens that appear at the same flow
 * position share an index (e.g. "seats" and "players" are both step 2, since
 * they're equivalent entry points for different track types).
 *
 * "location" uses a fractional index (2.5) because it is conditionally inserted
 * between "seats" (2) and "date" (3) for users without saved location info.
 * Without the fractional index, going location → date would compare as backward
 * and incorrectly slide down.
 */
const SCREEN_STEP_INDEX: Record<string, number> = {
  [SCREEN_SLUGS.SEATS]: 2,
  [SCREEN_SLUGS.PLAYERS]: 2,
  [SCREEN_SLUGS.VOUCHER]: 2.2,
  [SCREEN_SLUGS.LOCATION]: 2.5,
  [SCREEN_SLUGS.DATE]: 3,
  [SCREEN_SLUGS.SUMMARY]: 5,
  [SCREEN_SLUGS.EXTRAS]: 6,
  [SCREEN_SLUGS.CARD]: 2,
  [SCREEN_SLUGS.EXPERIENCE]: 3,
  [SCREEN_SLUGS.CART]: 4,
};

/**
 * Resolves the current step index from the URL route and screen params.
 * Step 0 = no route (home), step 1 = track selected but no screen.
 */
const getStepIndex = (selectedRoute?: string, selectedScreen?: string): number => {
  if (!selectedRoute) return 0;
  if (selectedRoute === ROUTE_SLUGS.TRACKS && !selectedScreen) return 1;
  if (!selectedScreen) return 1;
  return SCREEN_STEP_INDEX[selectedScreen] ?? 1;
};

/**
 * Declarative route → component mapping.
 * Each top-level key is a URL route slug; nested keys are screen slugs.
 * To add a new screen, add an entry here rather than extending the if/if chain.
 */
const ROUTE_COMPONENTS: Record<string, Record<string, React.ComponentType>> = {
  [ROUTE_SLUGS.MAIN]: {
    [SCREEN_SLUGS.SEATS]: SelectSeatsRaces,
    [SCREEN_SLUGS.DATE]: SelectDateTime,
    [SCREEN_SLUGS.SUMMARY]: ReviewExperience,
    [SCREEN_SLUGS.VOUCHER]: MainActiveKids,
    [SCREEN_SLUGS.LOCATION]: MainPostCode,
    [SCREEN_SLUGS.EXTRAS]: AddAnotherAttraction,
  },
  [ROUTE_SLUGS.INTERMEDIATE]: {
    [SCREEN_SLUGS.SEATS]: IntermediateTrackSelectSeatsRaces,
    [SCREEN_SLUGS.DATE]: IntermediateTrackSelectDateTime,
    [SCREEN_SLUGS.SUMMARY]: IntermediateTrackReviewExperience,
    [SCREEN_SLUGS.VOUCHER]: IntermediateTrackActiveKids,
    [SCREEN_SLUGS.LOCATION]: IntermediateTrackPostCode,
    [SCREEN_SLUGS.EXTRAS]: AddAnotherAttraction,
  },
  [ROUTE_SLUGS.MINI]: {
    [SCREEN_SLUGS.SEATS]: MiniSelectSeatsRaces,
    [SCREEN_SLUGS.DATE]: MiniSelectDateTime,
    [SCREEN_SLUGS.SUMMARY]: MiniReviewExperience,
    [SCREEN_SLUGS.VOUCHER]: MiniActiveKids,
    [SCREEN_SLUGS.LOCATION]: MiniPostCode,
    [SCREEN_SLUGS.EXTRAS]: AddAnotherAttraction,
  },
  [ROUTE_SLUGS.JUNIOR]: {
    [SCREEN_SLUGS.SEATS]: JuniorTrackSelectSeatsRaces,
    [SCREEN_SLUGS.DATE]: JuniorTrack,
    [SCREEN_SLUGS.SUMMARY]: JuniorTrackReviewExperience,
    [SCREEN_SLUGS.VOUCHER]: JuniorTrackActiveKids,
    [SCREEN_SLUGS.LOCATION]: JuniorTrackPostCode,
    [SCREEN_SLUGS.EXTRAS]: AddAnotherAttraction,
  },
  ...(BATTLEKART_ENABLED
    ? {
        [ROUTE_SLUGS.BATTLE_KART]: {
          [SCREEN_SLUGS.PLAYERS]: BattleKartPlayersAndSessions,
          [SCREEN_SLUGS.DATE]: BattleKart,
          [SCREEN_SLUGS.VOUCHER]: BattleKartActiveKids,
    [SCREEN_SLUGS.LOCATION]: BattleKartPostCode,
          [SCREEN_SLUGS.EXTRAS]: AddAnotherAttraction,
        },
      }
    : {}),
  [ROUTE_SLUGS.ARCADE]: {
    [SCREEN_SLUGS.CARD]: ArcadeCard,
    [SCREEN_SLUGS.EXPERIENCE]: ArcadeExperience,
    [SCREEN_SLUGS.CART]: ArcadeCart,
  },
};

/**
 * QueryBasedRouter — renders the active page component based on URL params
 * and wraps it in AnimatePresence for directional slide transitions.
 *
 * Direction tracking uses refs (not state) to avoid triggering extra renders.
 * The direction is recalculated synchronously during render so the correct
 * value is available before AnimatePresence needs it for the exit/enter cycle.
 *
 * Key architectural decisions:
 *   - `mode="wait"`: the exiting page fades out and is fully removed before
 *     the entering page mounts and slides in. Only one page is ever in the
 *     DOM, guaranteeing zero overlap. The wrapper's `overflow: hidden` clips
 *     the entering page as it slides into view.
 *   - `initial={false}`: no animation on the very first mount (page load).
 *   - `routeKey`: combines selectedRoute + selectedScreen to trigger transitions
 *     only when the actual page changes (not on param-only changes).
 *
 * Static UI elements (ProgressBar, BottomSummary) use React Portals to render
 * into containers outside this animated tree — see portal targets in NewUI.
 */
const QueryBasedRouter: React.FC = () => {
  const { getUrlParams } = useUrlParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackSelectionOption, isAnyItemsAvailableInCart, showArcadeBanner, appHostedAt } = useTrackContext();
  const params = getUrlParams();
  const hasCleanedRef = useRef(false);
  const prevStepRef = useRef<number | null>(null);
  const directionRef = useRef<number>(1);
  const prefersReducedMotion = usePrefersReducedMotion();
  const slideVariants = React.useMemo(() => pageSlideVariants(prefersReducedMotion), [prefersReducedMotion]);

  const currentStep = getStepIndex(params.selectedRoute, params.selectedScreen);

  if (prevStepRef.current !== null && currentStep !== prevStepRef.current) {
    const stateDirection = (location.state as { directionOverride?: number } | null)?.directionOverride;
    // While the experience selector is skipped, "/" (step 0) exists only to
    // forward to the racing age-selection screen, so every transition into or
    // out of it renders instantly (direction 0) — old links to "/" (back
    // buttons, breadcrumbs) become invisible pass-throughs instead of a
    // slide-out-and-back bounce.
    const involvesSkippedHome =
      (currentStep === 0 || prevStepRef.current === 0) && shouldSkipExperienceSelector({ isAnyItemsAvailableInCart, showArcadeBanner, appHostedAt });
    directionRef.current = stateDirection ?? (involvesSkippedHome ? 0 : currentStep > prevStepRef.current ? 1 : -1);
  }
  prevStepRef.current = currentStep;

  // In-app fallback for BattleKart URLs the pre-mount check in src/index.tsx
  // cannot catch (e.g. a stale BattleKart cart pill navigating in-flow). The
  // slug is the canonical one: legacy paths and the legacy
  // ?selectedRoute=battle-kart-track form are rewritten during bootstrap.
  useLayoutEffect(() => {
    if (!BATTLEKART_ENABLED && params.selectedRoute === ROUTE_SLUGS.BATTLE_KART) {
      window.location.replace(BATTLEKART_CLOSED_URL);
    }
  }, [params.selectedRoute]);

  useLayoutEffect(() => {
    if (params.selectedRoute === ROUTE_SLUGS.TRACKS && !hasCleanedRef.current) {
      const hasOtherParams = params.numberOfSeats || params.numberOfRaces || params.ageGroup || params.selectedScreen || params.postcode;

      if (hasOtherParams) {
        hasCleanedRef.current = true;
        // Keep preserved campaign/tracking params on the URL while clearing the
        // booking params this cleanup is meant to strip.
        navigate(appendPersistedQueryParams(`/${ROUTE_SLUGS.TRACKS}`), { replace: true });
      }
    }

    if (params.selectedRoute !== ROUTE_SLUGS.TRACKS) {
      hasCleanedRef.current = false;
    }
  }, [params, navigate]);

  const renderComponent = () => {
    const { selectedRoute, selectedScreen } = params;
    const visibility = getVisibilityConfig();

    if (!BATTLEKART_ENABLED && selectedRoute === ROUTE_SLUGS.BATTLE_KART) {
      // Blank frame while the closed-page redirect above takes over —
      // falling through would flash the experience selector first.
      return null;
    }

    if (selectedRoute === ROUTE_SLUGS.TRACKS) {
      if (visibility.racingTrackSelection === false) return <TrackSelection />;
      return <RacingTrackSelection />;
    }

    if (selectedRoute && selectedScreen) {
      if (selectedScreen === SCREEN_SLUGS.LOCATION && visibility.postCode === false) {
        return <TrackSelection />;
      }
      if (selectedScreen === SCREEN_SLUGS.EXTRAS && visibility.addAnotherAttraction === false) {
        return <TrackSelection />;
      }
      if (selectedRoute === ROUTE_SLUGS.ARCADE && visibility.arcade === false) {
        return <TrackSelection />;
      }
      if (selectedRoute === ROUTE_SLUGS.BATTLE_KART && visibility.battleKart === false) {
        return <TrackSelection />;
      }
      const Component = ROUTE_COMPONENTS[selectedRoute]?.[selectedScreen];
      if (Component) return <Component />;
    }

    return <TrackSelection />;
  };

  const routeKey = `${params.selectedRoute || "home"}-${params.selectedScreen || "index"}`;

  const isTransitioningRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prevRouteKeyRef = useRef(routeKey);

  useEffect(() => {
    BookingAnalytics.stepViewed({
      route: params.selectedRoute || "home",
      screen: params.selectedScreen || "index",
      track: trackSelectionOption.title || undefined,
    });
  }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (prevRouteKeyRef.current !== routeKey) {
      prevRouteKeyRef.current = routeKey;
      window.scrollTo(0, 0);
    }
  }, [routeKey]);

  const handleAnimationStart = useCallback((definition: string) => {
    if (definition === "exit") {
      isTransitioningRef.current = true;
      document.body.style.overflow = "hidden";
    }
  }, []);

  const handleAnimationComplete = useCallback((definition: string) => {
    if (definition === "center" && isTransitioningRef.current) {
      isTransitioningRef.current = false;
      document.body.style.overflow = "";
    }
  }, []);

  const handleExitComplete = useCallback(() => {
    if (wrapperRef.current) {
      wrapperRef.current.style.height = "";
    }
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: "relative", overflow: "hidden" }}>
      <AnimatePresence mode="wait" custom={directionRef.current} initial={false} onExitComplete={handleExitComplete}>
        <motion.div
          key={routeKey}
          /* Lets the theme style one screen's copy without touching the others:
             `#react-product [data-hlc-screen="main-seats"] .booking-label`.
             ProgressBar and BottomSummary render through portals outside this
             wrapper, so they are deliberately out of reach and stay uniform
             across screens. */
          data-hlc-screen={routeKey}
          custom={directionRef.current}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          style={{ position: "relative", width: "100%" }}
          onAnimationStart={handleAnimationStart}
          onAnimationComplete={handleAnimationComplete}
        >
          <Suspense fallback={<ScreenFallback />}>{renderComponent()}</Suspense>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/**
 * AnimatedBackground — brand-aware ambient backdrop over the black page.
 *
 * Two large, soft, blurred colour dots sit statically in opposite corners: one in
 * the top-left, one in the bottom-right (no motion, per client request). Their
 * colours come from the active brand, derived from the route (the same signal
 * ProgressBar uses): the BattleKart flow lives under `battlekart`,
 * everything else is HyperKarting. The layer is viewport-fixed and sits behind
 * all content (negative z-index), so it also shows through the transparent
 * fixed bottom bar; it never captures pointer events and clips the dots to the
 * viewport. `useLocation` makes it re-evaluate the brand on every in-app
 * navigation.
 */
const AnimatedBackground: React.FC = () => {
  // The pathway (hk/bk) is resolved from the route, and each pathway's config
  // carries its own dot colours, so the brand switch happens in the config
  // rather than here.
  const { pathwayStyle, isBattleKart } = usePathwayStyling();
  const dotColorA = pathwayStyle.ambientDotA ?? (isBattleKart ? "rgb(250, 198, 24)" : "rgb(5, 255, 250)");
  const dotColorB = pathwayStyle.ambientDotB ?? (isBattleKart ? "rgb(234, 88, 26)" : "rgb(249, 8, 255)");

  // A soft radial fade keeps the dot diffuse without an expensive large blur.
  const dotFill = (color: string) => `radial-gradient(circle, ${color} 0%, transparent 70%)`;

  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, zIndex: -1, pointerEvents: "none", overflow: "hidden" }}>
      <span className="brand-dot" style={{ background: dotFill(dotColorA), transform: "translate3d(calc(10vw - 50%), calc(10vh - 50%), 0)" }} />
      <span className="brand-dot" style={{ background: dotFill(dotColorB), transform: "translate3d(calc(90vw - 50%), calc(90vh - 50%), 0)" }} />
      {/* Last, so the grid reads at the same strength over the dots as over bare black. */}
      <span className="brand-grid" />
    </div>
  );
};

/**
 * NewUI — top-level layout with BrowserRouter and portal mount points.
 *
 * Layout structure:
 *   ┌─────────────────────────────────┐
 *   │  #progress-bar-portal           │  ← ProgressBar renders here via portal
 *   ├─────────────────────────────────┤
 *   │  <Routes>                       │
 *   │    └─ <QueryBasedRouter>        │
 *   │         └─ <AnimatePresence>    │  ← page content animates here
 *   │              └─ <motion.div>    │
 *   ├─────────────────────────────────┤
 *   │  #bottom-summary-portal         │  ← BottomSummary renders here via portal
 *   └─────────────────────────────────┘
 *
 * Portal targets are placed OUTSIDE the Routes/AnimatePresence tree so that:
 *   1. ProgressBar and BottomSummary are immune to slide/opacity animations.
 *   2. CSS `transform` on motion.div does not break `position: fixed` on BottomSummary.
 *      (A parent with `transform` creates a new containing block, making `fixed`
 *       positioning relative to that parent rather than the viewport.)
 *   3. Components persist across route transitions without unmounting/remounting.
 *
 * See `usePortalTarget` hook (src/hooks/usePortalTarget.ts) for safe portal
 * acquisition that handles first-render timing.
 */
const NewUI: React.FC = () => {
  // Already applied in src/index.tsx before render; this returns the memoised
  // basename — the serving page's own pathname (`/pages/booking`, or another
  // mount page), or undefined outside Shopify (local dev / headless embeds).
  const basename = applyCanonicalBookingUrl();

  return (
    <BrowserRouter basename={basename}>
      <AnimatedBackground />
      <div id="progress-bar-portal" />
      <ProgressBar />
      <Routes>
        <Route path="/" element={<QueryBasedRouter />} />
        <Route path="/:selectedRoute" element={<QueryBasedRouter />} />
        <Route path="/:selectedRoute/:selectedScreen" element={<QueryBasedRouter />} />
      </Routes>
      <div id="bottom-summary-portal" />
      <GlobalBottomSummary />
    </BrowserRouter>
  );
};

export default NewUI;
